import { strToU8, zipSync } from "fflate";
import { CRITTER_PROJECT_SNAPSHOT_FILENAME } from "../../core/migrations/projectSnapshot";
import {
  buildProjectManifest,
  CRITTER_PROJECT_MANIFEST_FILENAME,
} from "../../core/migrations/projectManifest";
import {
  collectResolvedXmlAssetsForZip,
  resolveAssetStoreEntryForPath,
} from "../../utils/environmentZipAssets";
import {
  ProjectAssetReadError,
  ProjectSha256UnavailableError,
  readProjectCreatedAtIsoFromFileMap,
} from "../../utils/environmentBundle";
import { showWebCryptoUnavailableToast } from "../../utils/browserCapabilityToasts";
import { sanitizeMjcfName } from "../../utils/mjcfNames";

const APP_VERSION = import.meta.env?.VITE_APP_VERSION ?? "0.0.0";

export function downloadTextFile(filename, text, type = "text/plain") {
  const blob = new Blob([text], { type });
  downloadBlob(filename, blob);
}

export function downloadBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function classifyProjectAssetRole(path) {
  const lower = String(path || "").toLowerCase();
  if (lower.endsWith(".obj") || lower.endsWith(".stl") || lower.endsWith(".dae")) return "mesh";
  if (
    lower.endsWith(".png") ||
    lower.endsWith(".jpg") ||
    lower.endsWith(".jpeg") ||
    lower.endsWith(".bmp") ||
    lower.endsWith(".tga") ||
    lower.endsWith(".webp") ||
    lower.endsWith(".ktx") ||
    lower.endsWith(".ktx2")
  ) {
    return "texture";
  }
  if (lower.endsWith(".mtl")) return "sidecar";
  return "asset";
}

async function sha256Hex(bytes) {
  if (!globalThis.crypto?.subtle) {
    showWebCryptoUnavailableToast("Project export");
    throw new ProjectSha256UnavailableError();
  }
  const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("");
}

function isNonIdentityVector(values, identity, epsilon = 1e-12) {
  if (!Array.isArray(values) || values.length !== identity.length) return false;
  return values.some((value, index) => Math.abs((Number(value) || 0) - identity[index]) > epsilon);
}

function finiteVector(values, identity) {
  return identity.map((fallback, index) => {
    const value = Number(values?.[index]);
    return Number.isFinite(value) ? value : fallback;
  });
}

function collectMeshUsage(sceneState) {
  const usageByMeshUuid = {};
  Object.values(sceneState?.bodies || {}).forEach((body) => {
    if (!body || !Array.isArray(body.geoms)) return;
    body.geoms.forEach((geom) => {
      const meshUuid = geom?.mesh ? String(geom.mesh) : "";
      if (!meshUuid) return;
      if (!usageByMeshUuid[meshUuid]) usageByMeshUuid[meshUuid] = [];
      usageByMeshUuid[meshUuid].push({
        bodyUuid: String(body.uuid || ""),
        bodyName: String(body.name || ""),
        geomUuid: String(geom.uuid || ""),
        geomName: String(geom.name || ""),
      });
    });
  });
  return usageByMeshUuid;
}

export function buildMeshOriginAdjustmentsSidecar(sceneState) {
  const meshes = sceneState?.meshes || {};
  const usageByMeshUuid = collectMeshUsage(sceneState);
  const entries = Object.entries(meshes)
    .map(([meshUuid, mesh]) => {
      const referencePosition = finiteVector(mesh?.referencePosition, [0, 0, 0]);
      const referenceOrientation = finiteVector(mesh?.referenceOrientation, [1, 0, 0, 0]);
      const hasAdjustment =
        isNonIdentityVector(referencePosition, [0, 0, 0]) ||
        isNonIdentityVector(referenceOrientation, [1, 0, 0, 0]);
      if (!hasAdjustment) return null;
      return {
        meshUuid: String(meshUuid),
        meshName: String(mesh?.name || meshUuid),
        referencePosition,
        referenceOrientation,
        usage: usageByMeshUuid[String(meshUuid)] || [],
      };
    })
    .filter(Boolean);

  if (entries.length === 0) return null;

  return {
    type: "critterapp-mesh-origin-adjustments",
    schemaVersion: 1,
    generatedAtIso: new Date().toISOString(),
    representation: {
      modelXml:
        "Mesh origin adjustments are baked into geom pos/quat in model.xml for MuJoCo compatibility.",
      critterProject:
        "Native Critter saves keep these values on scene.meshes[*].referencePosition/referenceOrientation.",
    },
    meshes: entries,
  };
}

export async function downloadPortableMjcfBundle({
  filename,
  xml,
  fileMap,
  folderName = "mujoco_xml_assets_bundle",
  sceneState,
}) {
  const assets = await collectResolvedXmlAssetsForZip(xml, fileMap || {});
  const meshOriginAdjustments = buildMeshOriginAdjustmentsSidecar(sceneState);
  const readme = `# MuJoCo XML Export

This bundle contains a MuJoCo-ready folder with the generated MJCF model and the asset files referenced by that XML.

## Files

- \`model.xml\` - Generated MJCF model.
- \`assets/\` - Meshes and textures referenced by \`model.xml\`.
${meshOriginAdjustments ? "- `mesh_origin_adjustments.json` - Critter provenance for mesh reference-frame adjustments baked into `model.xml`.\n" : ""}

Open \`model.xml\` from this folder in MuJoCo desktop. MJCF is the MuJoCo simulation model. It may not preserve every Critter editor, training, or workflow feature; use Save Project when you want to save and reopen the full Critter setup.
`;
  const zipEntries = {
    [`${folderName}/README.md`]: strToU8(readme),
    [`${folderName}/model.xml`]: strToU8(xml),
  };
  if (meshOriginAdjustments) {
    zipEntries[`${folderName}/mesh_origin_adjustments.json`] = strToU8(
      JSON.stringify(meshOriginAdjustments, null, 2)
    );
  }
  for (const asset of assets) {
    if (!asset.zipPath || asset.zipPath.toLowerCase() === "model.xml") continue;
    zipEntries[`${folderName}/${asset.zipPath}`] = new Uint8Array(await asset.file.arrayBuffer());
  }
  const zipBytes = zipSync(zipEntries, { level: 0 });
  downloadBlob(filename, new Blob([zipBytes], { type: "application/zip" }));
}

export function createStandaloneMjcfSceneXml(xml, projectName) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(String(xml || ""), "application/xml");
  if (doc.querySelector("parsererror")) return xml;
  const root = doc.documentElement;
  if (!root || root.tagName.toLowerCase() !== "mujoco") return xml;

  const modelName =
    sanitizeMjcfName(normalizeExportProjectName(projectName)).toLowerCase() || "untitled_scene";
  root.setAttribute("model", `${modelName}_scene`);
  applyInitKeyframeAsDefaultPose(doc);

  const findDirectChild = (tagName) =>
    Array.from(root.children).find((child) => child.tagName.toLowerCase() === tagName);
  let asset = findDirectChild("asset");
  let worldbody = Array.from(root.children).find(
    (child) => child.tagName.toLowerCase() === "worldbody"
  );
  if (!asset) {
    asset = doc.createElement("asset");
    root.insertBefore(asset, worldbody || null);
  }
  if (!worldbody) {
    worldbody = doc.createElement("worldbody");
    root.appendChild(worldbody);
  }

  if (!asset.querySelector('texture[name="scene_grid"]')) {
    const gridTexture = doc.createElement("texture");
    gridTexture.setAttribute("name", "scene_grid");
    gridTexture.setAttribute("type", "2d");
    gridTexture.setAttribute("builtin", "checker");
    gridTexture.setAttribute("rgb1", "0.72 0.72 0.72");
    gridTexture.setAttribute("rgb2", "0.52 0.52 0.52");
    gridTexture.setAttribute("width", "512");
    gridTexture.setAttribute("height", "512");
    gridTexture.setAttribute("mark", "edge");
    gridTexture.setAttribute("markrgb", "0.12 0.12 0.12");
    asset.insertBefore(gridTexture, asset.firstChild);
  }

  if (!asset.querySelector('material[name="scene_floor"]')) {
    const floorMaterial = doc.createElement("material");
    floorMaterial.setAttribute("name", "scene_floor");
    floorMaterial.setAttribute("texture", "scene_grid");
    floorMaterial.setAttribute("texrepeat", "12 12");
    floorMaterial.setAttribute("texuniform", "true");
    floorMaterial.setAttribute("rgba", "0.8 0.8 0.8 1");
    floorMaterial.setAttribute("reflectance", "0.05");
    asset.appendChild(floorMaterial);
  }

  let visual = findDirectChild("visual");
  if (!visual) {
    visual = doc.createElement("visual");
    root.insertBefore(visual, worldbody);
  }
  let headlight = Array.from(visual.children).find(
    (child) => child.tagName.toLowerCase() === "headlight"
  );
  if (!headlight) {
    headlight = doc.createElement("headlight");
    visual.insertBefore(headlight, visual.firstChild);
  }
  headlight.setAttribute("active", "1");
  headlight.setAttribute("ambient", "0.35 0.35 0.35");
  headlight.setAttribute("diffuse", "0.75 0.75 0.75");
  headlight.setAttribute("specular", "0.35 0.35 0.35");

  let floor = worldbody.querySelector('geom[name="floor"], geom[type="plane"]');
  if (!floor) {
    floor = doc.createElement("geom");
    floor.setAttribute("name", "floor");
    floor.setAttribute("type", "plane");
    floor.setAttribute("pos", "0 0 0");
    worldbody.insertBefore(floor, worldbody.firstChild);
  }
  floor.setAttribute("type", "plane");
  floor.setAttribute("size", "0 0 0.05");
  floor.setAttribute("material", "scene_floor");
  floor.setAttribute("rgba", "0.8 0.8 0.8 1");
  floor.setAttribute("group", "0");
  floor.setAttribute("contype", "1");
  floor.setAttribute("conaffinity", "15");
  floor.setAttribute("condim", "3");
  floor.setAttribute("friction", "1 0.01 0.001");

  let key = worldbody.querySelector('light[name="scene_key"]');
  if (!key) {
    key = doc.createElement("light");
    key.setAttribute("name", "scene_key");
    worldbody.insertBefore(key, worldbody.firstChild);
  }
  key.setAttribute("pos", "0 -4 5");
  key.setAttribute("dir", "0 1 -1");
  key.setAttribute("directional", "true");
  key.setAttribute("diffuse", "0.8 0.8 0.75");
  key.setAttribute("specular", "0.25 0.25 0.25");

  return new XMLSerializer().serializeToString(doc);
}

export function createRobotOnlyMjcfXml(xml, projectName) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(String(xml || ""), "application/xml");
  if (doc.querySelector("parsererror")) return xml;
  const root = doc.documentElement;
  if (!root || root.tagName.toLowerCase() !== "mujoco") return xml;

  const modelName =
    sanitizeMjcfName(normalizeExportProjectName(projectName)).toLowerCase() || "untitled_scene";
  root.setAttribute("model", modelName);
  applyInitKeyframeAsDefaultPose(doc);

  const worldbody = Array.from(root.children).find(
    (child) => child.tagName.toLowerCase() === "worldbody"
  );
  if (worldbody) {
    Array.from(worldbody.children).forEach((child) => {
      const tag = child.tagName.toLowerCase();
      const name = child.getAttribute("name") || "";
      if (tag === "geom" && name === "floor") child.remove();
      if (tag === "light" && (name === "spotlight" || name === "scene_key")) child.remove();
    });
  }

  return new XMLSerializer().serializeToString(doc);
}

export function applyInitKeyframeAsDefaultPose(doc) {
  const initKey = doc.querySelector('keyframe > key[name="init"]');
  const qpos = String(initKey?.getAttribute("qpos") || "")
    .trim()
    .split(/\s+/)
    .map(Number)
    .filter((value) => Number.isFinite(value));
  if (!qpos.length) return;

  const worldbody = doc.querySelector("mujoco > worldbody");
  if (!worldbody) return;

  let cursor = 0;
  const applyBody = (body) => {
    const children = Array.from(body.children);
    children.forEach((child) => {
      const tag = child.tagName.toLowerCase();
      if (tag === "freejoint") {
        if (cursor + 7 <= qpos.length) {
          body.setAttribute("pos", qpos.slice(cursor, cursor + 3).join(" "));
          body.setAttribute("quat", qpos.slice(cursor + 3, cursor + 7).join(" "));
        }
        cursor += 7;
        return;
      }
      if (tag !== "joint") return;
      const type = String(child.getAttribute("type") || "hinge").toLowerCase();
      if (type === "free") {
        if (cursor + 7 <= qpos.length) {
          body.setAttribute("pos", qpos.slice(cursor, cursor + 3).join(" "));
          body.setAttribute("quat", qpos.slice(cursor + 3, cursor + 7).join(" "));
        }
        cursor += 7;
      } else if (type === "ball") {
        cursor += 4;
      } else {
        if (cursor < qpos.length) child.setAttribute("ref", String(qpos[cursor]));
        cursor += 1;
      }
    });
    children.forEach((child) => {
      if (child.tagName.toLowerCase() === "body") applyBody(child);
    });
  };

  Array.from(worldbody.children).forEach((child) => {
    if (child.tagName.toLowerCase() === "body") applyBody(child);
  });
}

export async function downloadPortableMjcfSceneBundle({
  filename,
  xml,
  fileMap,
  folderName = "mujoco_xml_assets_bundle",
  projectName,
  sceneState,
  floorLiftAmount = 0,
}) {
  const lift = Number(floorLiftAmount) || 0;
  const robotBaseXml = createRobotOnlyMjcfXml(xml, projectName);
  const sceneBaseXml = createStandaloneMjcfSceneXml(xml, projectName);
  const robotXml = lift > 0 ? liftMjcfWorldBodies(robotBaseXml, lift) : robotBaseXml;
  const sceneXml = lift > 0 ? liftMjcfWorldBodies(sceneBaseXml, lift) : sceneBaseXml;
  const assets = await collectResolvedXmlAssetsForZip(xml, fileMap || {});
  const meshOriginAdjustments = buildMeshOriginAdjustmentsSidecar(sceneState);
  const readme = `# MuJoCo XML Export

This bundle contains a MuJoCo-ready folder with the generated MJCF model and the asset files referenced by that XML.

## Files

- \`model.xml\` - Desktop-friendly scene with a simple floor and light.
- \`scene.xml\` - Same desktop-friendly scene.
- \`robot.xml\` - Robot-only MJCF export without the added scene floor/light.
- \`assets/\` - Meshes and textures referenced by the XML files.
${meshOriginAdjustments ? "- `mesh_origin_adjustments.json` - Critter provenance for mesh reference-frame adjustments baked into the XML files.\n" : ""}

Open \`model.xml\` or \`scene.xml\` from this folder in MuJoCo desktop. Use \`robot.xml\` when you want to place the robot into another MuJoCo scene.
`;
  const zipEntries = {
    [`${folderName}/README.md`]: strToU8(readme),
    [`${folderName}/model.xml`]: strToU8(sceneXml),
    [`${folderName}/scene.xml`]: strToU8(sceneXml),
    [`${folderName}/robot.xml`]: strToU8(robotXml),
  };
  if (meshOriginAdjustments) {
    zipEntries[`${folderName}/mesh_origin_adjustments.json`] = strToU8(
      JSON.stringify(meshOriginAdjustments, null, 2)
    );
  }
  for (const asset of assets) {
    if (!asset.zipPath || asset.zipPath.toLowerCase() === "model.xml") continue;
    zipEntries[`${folderName}/${asset.zipPath}`] = new Uint8Array(await asset.file.arrayBuffer());
  }
  const zipBytes = zipSync(zipEntries, { level: 0 });
  downloadBlob(filename, new Blob([zipBytes], { type: "application/zip" }));
}

export function normalizeExportProjectName(value) {
  const trimmed = String(value || "").trim();
  return trimmed || "Untitled Scene";
}

function isDefaultProjectName(value) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  return normalized === "" || normalized === "untitled scene";
}

function getWorldBodyUuid(worldBody) {
  if (!worldBody) return "";
  return typeof worldBody === "string" ? worldBody : String(worldBody.uuid || "");
}

function formatSceneObjectName(value) {
  const normalized = String(value || "")
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
  if (!normalized) return "";
  return normalized.replace(/\b[a-z]/g, (letter) => letter.toUpperCase());
}

function isGenericWorldBodyName(value) {
  return /^object\s+\d+$/i.test(String(value || "").trim());
}

function getSceneObjectExportNames(sceneState) {
  const bodies = sceneState?.bodies || {};
  const worldBodies = Array.isArray(sceneState?.worldBodies) ? sceneState.worldBodies : [];
  return worldBodies
    .map((worldBody) => {
      const uuid = getWorldBodyUuid(worldBody);
      const worldName = typeof worldBody === "string" ? "" : worldBody?.name;
      const bodyName = bodies?.[uuid]?.name;
      return formatSceneObjectName(
        worldName && !isGenericWorldBodyName(worldName) ? worldName : bodyName || worldName
      );
    })
    .filter(Boolean);
}

export function suggestExportProjectName(projectName, sceneState) {
  if (!isDefaultProjectName(projectName)) return normalizeExportProjectName(projectName);

  const objectNames = getSceneObjectExportNames(sceneState);
  if (objectNames.length === 0) return "Blank Scene";
  if (objectNames.length === 1) return objectNames[0];
  if (objectNames.length === 2) return `${objectNames[0]} and ${objectNames[1]}`;
  return `${objectNames[0]}, ${objectNames[1]}, and ${objectNames.length - 2} More`;
}

export function toExportFilenameBase(projectName) {
  const sanitized = sanitizeMjcfName(normalizeExportProjectName(projectName)).toLowerCase();
  return sanitized || "untitled_scene";
}

export function applyProjectNameToMjcfXml(xml, projectName) {
  const source = String(xml || "");
  if (!source) return source;
  const modelName =
    sanitizeMjcfName(normalizeExportProjectName(projectName)).toLowerCase() || "untitled_scene";
  const openTagMatch = source.match(/<mujoco\b[^>]*>/i);
  if (!openTagMatch) return source;
  const openTag = openTagMatch[0];
  const updatedTag = /model\s*=/.test(openTag)
    ? openTag.replace(/(model\s*=\s*")[^"]*(")/i, `$1${modelName}$2`)
    : openTag.replace("<mujoco", `<mujoco model="${modelName}"`);
  return source.replace(openTag, updatedTag);
}

function byteLengthOfJson(value) {
  return strToU8(JSON.stringify(value, null, 2)).byteLength;
}

export async function estimateEnvironmentBundleSize({
  xml,
  sceneConfig,
  projectSnapshot,
  projectName,
  fileMap,
  includeAssets = true,
}) {
  let totalBytes =
    strToU8(xml || "").byteLength +
    byteLengthOfJson(sceneConfig || {}) +
    byteLengthOfJson(projectSnapshot || {});

  let assetCount = 0;
  let assetBytes = 0;
  const assetManifest = [];
  if (includeAssets) {
    const assets = await collectResolvedXmlAssetsForZip(xml || "", fileMap || {});
    for (const asset of assets) {
      if (!asset.zipPath || asset.zipPath.toLowerCase() === "model.xml") continue;
      const byteLength = Number(asset.file?.size ?? 0);
      assetCount += 1;
      assetBytes += byteLength;
      totalBytes += byteLength;
      assetManifest.push({
        path: asset.zipPath,
        role: classifyProjectAssetRole(asset.zipPath),
        referencedBy: ["model.xml"],
        byteLength,
      });
    }
  }

  const projectManifest = buildProjectManifest({
    appVersion: APP_VERSION,
    projectName: projectName || "Untitled Scene",
    createdAtIso: undefined,
    projectSnapshotPath: CRITTER_PROJECT_SNAPSHOT_FILENAME,
    modelXmlPath: "model.xml",
    sceneConfigPath: "scene_config.json",
    assetsRoot: "assets/",
    assets: assetManifest,
    integrity: { policy: "best-effort", complete: false, reason: "user-override" },
  });
  totalBytes += byteLengthOfJson(projectManifest);

  return {
    totalBytes,
    totalSizeKb: Math.max(1, Math.round(totalBytes / 1024)),
    assetBytes,
    assetSizeKb: Math.max(0, Math.round(assetBytes / 1024)),
    assetCount,
  };
}

export function formatApproxFileSize(bytes) {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return "Unknown";
  if (value >= 1024 * 1024) {
    const mb = value / (1024 * 1024);
    return `${mb >= 10 ? Math.round(mb) : mb.toFixed(1)} MB`;
  }
  return `${Math.max(1, Math.round(value / 1024))} KB`;
}

export async function downloadEnvironmentBundleFromExport({
  filename,
  xml,
  sceneConfig,
  projectSnapshot,
  projectName,
  fileMap,
  includeAssets,
  allowUnverifiableLocalProject = false,
  originalIntegrityReason,
}) {
  const zipEntries = {
    [CRITTER_PROJECT_SNAPSHOT_FILENAME]: strToU8(JSON.stringify(projectSnapshot, null, 2)),
    "model.xml": strToU8(xml),
    "scene_config.json": strToU8(JSON.stringify(sceneConfig, null, 2)),
  };
  const assetManifest = [];
  if (includeAssets) {
    for (const asset of await collectResolvedXmlAssetsForZip(xml, fileMap || {})) {
      if (!asset.zipPath || asset.zipPath.toLowerCase() === "model.xml") continue;
      let bytes;
      try {
        bytes = await asset.file.arrayBuffer();
      } catch (error) {
        throw new ProjectAssetReadError(asset.zipPath, error);
      }
      let sha256;
      try {
        // Hash the original asset bytes before zip compression. project.json hashes are
        // over the logical asset file, not whatever compressed bytes fflate may emit.
        sha256 = await sha256Hex(bytes);
      } catch (error) {
        if (!allowUnverifiableLocalProject) throw error;
      }
      assetManifest.push({
        path: asset.zipPath,
        role: classifyProjectAssetRole(asset.zipPath),
        referencedBy: ["model.xml"],
        byteLength: bytes.byteLength,
        ...(sha256 ? { sha256 } : {}),
      });
      zipEntries[asset.zipPath] = new Uint8Array(bytes);
    }
  }
  const integrityComplete = assetManifest.every((asset) => Boolean(asset.sha256));
  const projectManifest = buildProjectManifest({
    appVersion: APP_VERSION,
    projectName: projectName || "Untitled Scene",
    createdAtIso: await readProjectCreatedAtIsoFromFileMap(fileMap || {}),
    projectSnapshotPath: CRITTER_PROJECT_SNAPSHOT_FILENAME,
    modelXmlPath: "model.xml",
    sceneConfigPath: "scene_config.json",
    assetsRoot: "assets/",
    assets: assetManifest,
    integrity: integrityComplete
      ? { policy: "sha256-required", complete: true }
      : {
          policy: "best-effort",
          complete: false,
          reason: "user-override",
          ...(originalIntegrityReason
            ? { details: { originalReason: originalIntegrityReason } }
            : {}),
        },
  });
  zipEntries[CRITTER_PROJECT_MANIFEST_FILENAME] = strToU8(JSON.stringify(projectManifest, null, 2));
  const zipBytes = zipSync(zipEntries, { level: 0 });
  downloadBlob(filename, new Blob([zipBytes], { type: "application/zip" }));
}

export function getProjectIntegrityFailureReason(error) {
  if (error instanceof ProjectSha256UnavailableError) return "sha256-unavailable";
  if (error instanceof ProjectAssetReadError) return "asset-read-error";
  return null;
}

export function confirmUnverifiableLocalProjectSave(reason) {
  const detail =
    reason === "sha256-unavailable"
      ? "SHA-256 hashing is unavailable in this context. It usually requires HTTPS or localhost."
      : reason === "asset-read-error"
        ? "Critter could not read one or more asset files while preparing hashes."
        : "Critter could not fully verify the project assets.";
  return window.confirm(
    `${detail}\n\nYou can save an unverifiable local project, but it may not be accepted for cloud training until it is repaired or re-saved with full SHA-256 hashes.\n\nSave unverifiable local project?`
  );
}

function normalizeExportAssetPath(path) {
  return String(path || "")
    .trim()
    .replace(/\\/g, "/")
    .replace(/^\/+/, "");
}

function fileMapHasExportAsset(fileMap, assetPath) {
  const normalized = normalizeExportAssetPath(assetPath);
  if (!normalized) return true;
  const basename = normalized.split("/").pop();
  const hasFileMapEntry = Object.keys(fileMap || {}).some((key) => {
    const normalizedKey = normalizeExportAssetPath(key);
    return (
      normalizedKey === normalized ||
      normalizedKey.endsWith(`/${normalized}`) ||
      normalizedKey === basename
    );
  });
  if (hasFileMapEntry) return true;
  return Boolean(
    resolveAssetStoreEntryForPath(normalized) || resolveAssetStoreEntryForPath(basename)
  );
}

function formatListForWarning(items) {
  const labels = [...new Set(items.map((item) => String(item || "").trim()).filter(Boolean))];
  if (labels.length === 0) return "";
  if (labels.length <= 4) return labels.join(", ");
  return `${labels.slice(0, 4).join(", ")}, and ${labels.length - 4} more`;
}

const parseNumberList = (value, fallback = []) => {
  const raw = String(value || "").trim();
  if (!raw) return fallback;
  const nums = raw
    .split(/\s+/)
    .map(Number)
    .filter((n) => Number.isFinite(n));
  return nums.length > 0 ? nums : fallback;
};

const formatMjcfNumber = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  const rounded = Math.abs(n) < 1e-12 ? 0 : n;
  return Number(rounded.toFixed(12)).toString();
};

const getDirectChildrenByTag = (element, tagName) =>
  Array.from(element?.children || []).filter(
    (child) => String(child?.tagName || "").toLowerCase() === tagName
  );

const quatNormalize = (q) => {
  const values = Array.isArray(q) && q.length >= 4 ? q.map(Number) : [1, 0, 0, 0];
  const mag = Math.hypot(values[0], values[1], values[2], values[3]);
  if (!Number.isFinite(mag) || mag < 1e-12) return [1, 0, 0, 0];
  return [values[0] / mag, values[1] / mag, values[2] / mag, values[3] / mag];
};

const quatProductRaw = (a, b) => [
  a[0] * b[0] - a[1] * b[1] - a[2] * b[2] - a[3] * b[3],
  a[0] * b[1] + a[1] * b[0] + a[2] * b[3] - a[3] * b[2],
  a[0] * b[2] - a[1] * b[3] + a[2] * b[0] + a[3] * b[1],
  a[0] * b[3] + a[1] * b[2] - a[2] * b[1] + a[3] * b[0],
];

const quatMultiply = (aRaw, bRaw) => {
  const [aw, ax, ay, az] = quatNormalize(aRaw);
  const [bw, bx, by, bz] = quatNormalize(bRaw);
  return [
    aw * bw - ax * bx - ay * by - az * bz,
    aw * bx + ax * bw + ay * bz - az * by,
    aw * by - ax * bz + ay * bw + az * bx,
    aw * bz + ax * by - ay * bx + az * bw,
  ];
};

const quatRotateVec = (qRaw, vRaw) => {
  const q = quatNormalize(qRaw);
  const v = [0, Number(vRaw?.[0]) || 0, Number(vRaw?.[1]) || 0, Number(vRaw?.[2]) || 0];
  const qi = [q[0], -q[1], -q[2], -q[3]];
  const rotated = quatProductRaw(quatProductRaw(q, v), qi);
  return [rotated[1], rotated[2], rotated[3]];
};

const addVec3 = (a, b) => [
  (Number(a?.[0]) || 0) + (Number(b?.[0]) || 0),
  (Number(a?.[1]) || 0) + (Number(b?.[1]) || 0),
  (Number(a?.[2]) || 0) + (Number(b?.[2]) || 0),
];

const composeChildPose = (parent, localPos, localQuat) => {
  const rotatedPos = quatRotateVec(parent.quat, localPos);
  return {
    pos: addVec3(parent.pos, rotatedPos),
    quat: quatMultiply(parent.quat, localQuat),
  };
};

const geomContactEnabled = (geom) => {
  const contypeAttr = geom.getAttribute("contype");
  const conaffinityAttr = geom.getAttribute("conaffinity");
  const contype = contypeAttr == null ? 1 : Number(contypeAttr);
  const conaffinity = conaffinityAttr == null ? 1 : Number(conaffinityAttr);
  return (
    (Number.isFinite(contype) ? contype : 1) !== 0 ||
    (Number.isFinite(conaffinity) ? conaffinity : 1) !== 0
  );
};

const estimatePrimitiveGeomMinZ = (geom, pose, bodyPose) => {
  const type = String(geom.getAttribute("type") || "sphere").toLowerCase();
  if (type === "plane" || type === "hfield" || type === "mesh") return null;
  const size = parseNumberList(geom.getAttribute("size"), [0]);
  const fromto = parseNumberList(geom.getAttribute("fromto"), []);
  if (fromto.length >= 6 && (type === "capsule" || type === "cylinder")) {
    const radius = Number(size[0]) || 0;
    const a = addVec3(bodyPose.pos, quatRotateVec(bodyPose.quat, fromto.slice(0, 3)));
    const b = addVec3(bodyPose.pos, quatRotateVec(bodyPose.quat, fromto.slice(3, 6)));
    return Math.min(a[2], b[2]) - radius;
  }

  if (type === "sphere") return pose.pos[2] - (Number(size[0]) || 0);

  const worldX = quatRotateVec(pose.quat, [1, 0, 0]);
  const worldY = quatRotateVec(pose.quat, [0, 1, 0]);
  const worldZ = quatRotateVec(pose.quat, [0, 0, 1]);
  if (type === "box") {
    return (
      pose.pos[2] -
      Math.abs(worldX[2]) * (Number(size[0]) || 0) -
      Math.abs(worldY[2]) * (Number(size[1]) || 0) -
      Math.abs(worldZ[2]) * (Number(size[2]) || 0)
    );
  }
  if (type === "ellipsoid") {
    return (
      pose.pos[2] -
      Math.sqrt(
        (worldX[2] * (Number(size[0]) || 0)) ** 2 +
          (worldY[2] * (Number(size[1]) || 0)) ** 2 +
          (worldZ[2] * (Number(size[2]) || 0)) ** 2
      )
    );
  }
  if (type === "capsule" || type === "cylinder") {
    const radius = Number(size[0]) || 0;
    const halfLength = Number(size[1]) || 0;
    const axisZ = Math.abs(worldZ[2]);
    const radialZ = Math.sqrt(Math.max(0, 1 - axisZ * axisZ));
    return pose.pos[2] - axisZ * halfLength - radialZ * radius;
  }
  return null;
};

export function analyzeMjcfFloorClearance(xml) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xml || "", "application/xml");
  if (doc.querySelector("parsererror")) return null;
  const worldbody = doc.querySelector("mujoco > worldbody");
  if (!worldbody) return null;
  const floor = Array.from(worldbody.querySelectorAll(":scope > geom")).find((geom) => {
    const name = String(geom.getAttribute("name") || "").toLowerCase();
    const type = String(geom.getAttribute("type") || "").toLowerCase();
    return name === "floor" || type === "plane" || type === "hfield";
  });
  const floorZ = parseNumberList(floor?.getAttribute("pos"), [0, 0, 0])[2] || 0;
  let lowest = null;

  const visitBody = (body, parentPose) => {
    const localPos = parseNumberList(body.getAttribute("pos"), [0, 0, 0]);
    const localQuat = parseNumberList(body.getAttribute("quat"), [1, 0, 0, 0]);
    const bodyPose = composeChildPose(parentPose, localPos, localQuat);
    getDirectChildrenByTag(body, "geom").forEach((geom) => {
      if (!geomContactEnabled(geom)) return;
      const geomPos = parseNumberList(geom.getAttribute("pos"), [0, 0, 0]);
      const geomQuat = parseNumberList(geom.getAttribute("quat"), [1, 0, 0, 0]);
      const geomPose = composeChildPose(bodyPose, geomPos, geomQuat);
      const minZ = estimatePrimitiveGeomMinZ(geom, geomPose, bodyPose);
      if (minZ == null || !Number.isFinite(minZ)) return;
      if (!lowest || minZ < lowest.minZ) {
        lowest = {
          minZ,
          bodyName: body.getAttribute("name") || "(unnamed body)",
          geomName: geom.getAttribute("name") || "(unnamed geom)",
          geomType: geom.getAttribute("type") || "sphere",
        };
      }
    });
    getDirectChildrenByTag(body, "body").forEach((child) => visitBody(child, bodyPose));
  };

  getDirectChildrenByTag(worldbody, "body").forEach((body) =>
    visitBody(body, { pos: [0, 0, 0], quat: [1, 0, 0, 0] })
  );

  if (!lowest) return null;
  const clearance = lowest.minZ - floorZ;
  return {
    floorZ,
    clearance,
    liftAmount: clearance < 0 ? -clearance : 0,
    bodyName: lowest.bodyName,
    geomName: lowest.geomName,
    geomType: lowest.geomType,
  };
}

export function liftMjcfWorldBodies(xml, liftAmount) {
  const lift = Number(liftAmount);
  if (!Number.isFinite(lift) || lift <= 0) return xml;
  const parser = new DOMParser();
  const doc = parser.parseFromString(xml || "", "application/xml");
  if (doc.querySelector("parsererror")) return xml;
  const worldbody = doc.querySelector("mujoco > worldbody");
  if (!worldbody) return xml;
  getDirectChildrenByTag(worldbody, "body").forEach((body) => {
    const pos = parseNumberList(body.getAttribute("pos"), [0, 0, 0]);
    const next = [pos[0] || 0, pos[1] || 0, (pos[2] || 0) + lift];
    body.setAttribute("pos", next.map(formatMjcfNumber).join(" "));
  });
  return new XMLSerializer().serializeToString(doc);
}

function appendGeneratedExportWarnings(warnings, exportWarnings) {
  const seen = new Set(warnings);
  const omittedGeomClasses = new Map();

  (Array.isArray(exportWarnings) ? exportWarnings : []).forEach((warning) => {
    const normalized = String(warning || "").trim();
    if (!normalized) return;

    const omittedGeomClassMatch = normalized.match(
      /^Omitted geom class "([^"]+)" on "([^"]+)" because the generated XML has no matching default class\.$/
    );
    if (omittedGeomClassMatch) {
      const [, className, geomName] = omittedGeomClassMatch;
      const existing = omittedGeomClasses.get(className) || [];
      existing.push(geomName);
      omittedGeomClasses.set(className, existing);
      return;
    }

    if (seen.has(normalized)) return;
    seen.add(normalized);
    warnings.push(normalized);
  });

  omittedGeomClasses.forEach((geomNames, className) => {
    const count = geomNames.length;
    const geomList = formatListForWarning(geomNames);
    const message = `Some source XML defaults named "${className}" were not included in the MuJoCo export${geomList ? ` for ${geomList}` : ""}. ${count === 1 ? "That part" : "Those parts"} will use standard geometry settings instead.`;
    if (seen.has(message)) return;
    seen.add(message);
    warnings.push(message);
  });
}

export function buildMjcfExportReview(xml, fileMap, exportWarnings = []) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xml || "", "application/xml");
  const parseError = doc.querySelector("parsererror");
  const root = doc.documentElement;
  const blockers = [];
  const warnings = [];
  const bodyCount = doc.querySelectorAll("worldbody body").length;
  const jointCount = doc.querySelectorAll("worldbody joint").length;
  const actuatorCount = doc.querySelectorAll("actuator > *").length;

  if (!xml || !xml.trim()) {
    blockers.push("The generated XML was empty.");
  }
  if (parseError) {
    blockers.push("The generated XML could not be read.");
  }
  if (!root || root.tagName !== "mujoco") {
    blockers.push("The exported file does not look like a MuJoCo XML file.");
  }
  if (!doc.querySelector("worldbody")) {
    blockers.push("The exported file is missing its world body section.");
  }
  if (bodyCount === 0) {
    warnings.push("No robot bodies were found in the export.");
  }

  const assetPaths = Array.from(
    doc.querySelectorAll("asset mesh[file], asset texture[file], asset hfield[file]")
  )
    .map((el) => el.getAttribute("file"))
    .filter(Boolean);
  const missingAssets = assetPaths.filter(
    (assetPath) => !fileMapHasExportAsset(fileMap, assetPath)
  );
  if (missingAssets.length > 0) {
    warnings.push(`${missingAssets.length} referenced asset file(s) may be missing.`);
  }
  const floorClearance = analyzeMjcfFloorClearance(xml);
  if (floorClearance?.liftAmount > 1e-6) {
    warnings.push(
      `Some exported collision geometry is below the floor by about ${formatMjcfNumber(floorClearance.liftAmount)} m (${floorClearance.bodyName}/${floorClearance.geomName}).`
    );
  }
  appendGeneratedExportWarnings(warnings, exportWarnings);

  const status =
    blockers.length > 0 ? "blocked" : warnings.length > 0 ? "ready_with_warnings" : "ready";
  const summary =
    status === "ready"
      ? "The XML was generated and basic checks passed."
      : status === "ready_with_warnings"
        ? "The XML was generated, but a few things may need review."
        : "The XML was generated, but it needs review before use.";

  return {
    status,
    summary,
    counts: {
      warnings: warnings.length,
      bodies: bodyCount,
      joints: jointCount,
      actuators: actuatorCount,
      referencedAssets: assetPaths.length,
      missingAssets: missingAssets.length,
      fileSizeKb: Math.max(1, Math.round(new Blob([xml || ""]).size / 1024)),
    },
    blockers,
    warnings,
    floorClearance,
  };
}

const NO_ROBOT_BODIES_WARNING = "No robot bodies were found in the export.";

export function simplifyBlankProjectReview(review) {
  const counts = review?.counts || {};
  const warnings = review?.warnings || [];
  const onlyBlankSceneWarning =
    warnings.length === 0 || (warnings.length === 1 && warnings[0] === NO_ROBOT_BODIES_WARNING);
  const isBlankProject =
    (counts.bodies ?? 0) === 0 &&
    (counts.joints ?? 0) === 0 &&
    (counts.actuators ?? 0) === 0 &&
    (counts.referencedAssets ?? 0) === 0 &&
    (counts.missingAssets ?? 0) === 0 &&
    (review?.blockers || []).length === 0 &&
    onlyBlankSceneWarning;

  if (!isBlankProject) return review;

  return {
    ...review,
    status: "ready",
    summary: "This project is empty. Save it only if you want to keep a blank Critter project.",
    warnings: [],
    counts: {
      ...counts,
      warnings: 0,
      isBlankProject: true,
    },
  };
}

export function appendExportReviewWarning(review, warning) {
  const message = String(warning || "").trim();
  if (!message || (review.warnings || []).includes(message)) return review;
  const warnings = [...(review.warnings || []), message];
  const status = (review.blockers || []).length > 0 ? "blocked" : "ready_with_warnings";
  return {
    ...review,
    status,
    summary:
      status === "blocked"
        ? review.summary
        : "The XML was generated, but a few things may need review.",
    warnings,
    counts: {
      ...(review.counts || {}),
      warnings: warnings.length,
    },
  };
}
